import React, { useEffect, useState } from "react";
import axios from "axios";
import { Check, Calendar, Sparkles, ShieldCheck, MapPin, Mail } from "lucide-react";
import { FadeUpGroup } from "../components/FadeUpGroup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INDUSTRIES = [
    "Retail",
    "Real Estate",
    "Healthcare",
    "Manufacturing",
    "Services",
    "Other",
];

const PRODUCTS = [
    "Mativex",
    "Vexen",
    "Karya",
    "Vyaahar",
    "Attestra",
    "Acrium",
    "Kretara",
];

const initialForm = {
    name: "",
    company: "",
    email: "",
    phone: "",
    industry: "",
    products: [],
    message: "",
};

const validate = (f) => {
    const e = {};
    if (!f.name || f.name.trim().length < 2) e.name = "Please share your full name.";
    if (!f.company || f.company.trim().length < 1) e.company = "Company name is required.";
    if (!f.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email))
        e.email = "Enter a valid work email.";
    if (f.phone && f.phone.replace(/\D/g, "").length < 6)
        e.phone = "Please enter a valid phone.";
    return e;
};

export const ContactPage = () => {
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [serverError, setServerError] = useState("");

    // Prefill products from `?products=mativex,karya` query string. Used by
    // the Pricing calculator's "Talk to us about this bundle" CTA.
    useEffect(() => {
        if (typeof window === "undefined") return;
        const params = new URLSearchParams(window.location.search);
        const raw = params.get("products");
        if (!raw) return;
        const slugs = raw
            .split(",")
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean);
        if (!slugs.length) return;
        // Match against the canonical option names rendered on the page.
        const SLUG_TO_NAME = {
            mativex: "Mativex",
            vexen: "Vexen",
            karya: "Karya",
            vyaahar: "Vyaahar",
            attestra: "Attestra",
            acrium: "Acrium",
            kretara: "Kretara",
        };
        const names = slugs.map((s) => SLUG_TO_NAME[s]).filter(Boolean);
        if (names.length) {
            setForm((f) => ({ ...f, products: names }));
        }
    }, []);

    const set = (k, v) => {
        setForm((s) => ({ ...s, [k]: v }));
        if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const toggleProduct = (p) => {
        setForm((s) =>
            s.products.includes(p)
                ? { ...s, products: s.products.filter((x) => x !== p) }
                : { ...s, products: [...s.products, p] }
        );
    };

    const submit = async (e) => {
        e.preventDefault();
        setServerError("");
        const errs = validate(form);
        if (Object.keys(errs).length) {
            setErrors(errs);
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/leads`, form, {
                headers: { "Content-Type": "application/json" },
                timeout: 15000,
            });
            setSuccess(true);
            setForm(initialForm);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setServerError(
                typeof detail === "string"
                    ? detail
                    : "Something went wrong. Please try again or email hello@scitus.ai."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="section" id="contact" data-testid="contact-page">
            <div className="container">
                <div className="section-head">
                    <span className="eyebrow">Contact</span>
                    <h1 className="h1" style={{ marginTop: 18 }}>
                        Book a 30-minute Scitus demo
                    </h1>
                    <p>
                        Tell us about your business. We&apos;ll tailor the demo
                        to the products and industry that matter to you.
                    </p>
                </div>

                <FadeUpGroup>
                    <div className="contact-grid">
                        <div className="form-card fade-up" data-testid="contact-form-card">
                            {success ? (
                                <div className="form-success" data-testid="contact-success">
                                    <div className="check-circle">
                                        <Check size={32} />
                                    </div>
                                    <h2 className="h2" style={{ fontSize: "1.6rem" }}>
                                        Thanks — request received
                                    </h2>
                                    <p className="lead mt-4">
                                        A Scitus team member will reach out
                                        within one business day to schedule your
                                        demo. In the meantime, feel free to
                                        explore our products.
                                    </p>
                                    <div style={{ marginTop: 24 }}>
                                        <a
                                            href="/products"
                                            className="btn btn-ghost"
                                            data-testid="contact-success-explore-btn"
                                        >
                                            Explore products
                                        </a>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            style={{ marginLeft: 12 }}
                                            onClick={() => setSuccess(false)}
                                            data-testid="contact-success-new-btn"
                                        >
                                            Send another
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <form
                                    onSubmit={submit}
                                    noValidate
                                    data-testid="contact-form"
                                >
                                    <div className="field-group two">
                                        <div className="field">
                                            <label htmlFor="name">Full name</label>
                                            <input
                                                id="name"
                                                type="text"
                                                value={form.name}
                                                onChange={(e) => set("name", e.target.value)}
                                                placeholder="Arjun Mehta"
                                                className={errors.name ? "invalid" : ""}
                                                data-testid="contact-input-name"
                                            />
                                            {errors.name && (
                                                <div className="err">{errors.name}</div>
                                            )}
                                        </div>
                                        <div className="field">
                                            <label htmlFor="company">Company</label>
                                            <input
                                                id="company"
                                                type="text"
                                                value={form.company}
                                                onChange={(e) => set("company", e.target.value)}
                                                placeholder="Northwind Retail"
                                                className={errors.company ? "invalid" : ""}
                                                data-testid="contact-input-company"
                                            />
                                            {errors.company && (
                                                <div className="err">{errors.company}</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="field-group two">
                                        <div className="field">
                                            <label htmlFor="email">Work email</label>
                                            <input
                                                id="email"
                                                type="email"
                                                value={form.email}
                                                onChange={(e) => set("email", e.target.value)}
                                                placeholder="arjun@northwind.com"
                                                className={errors.email ? "invalid" : ""}
                                                data-testid="contact-input-email"
                                            />
                                            {errors.email && (
                                                <div className="err">{errors.email}</div>
                                            )}
                                        </div>
                                        <div className="field">
                                            <label htmlFor="phone">Phone (optional)</label>
                                            <input
                                                id="phone"
                                                type="tel"
                                                value={form.phone}
                                                onChange={(e) => set("phone", e.target.value)}
                                                placeholder="+91 90000 12345"
                                                className={errors.phone ? "invalid" : ""}
                                                data-testid="contact-input-phone"
                                            />
                                            {errors.phone && (
                                                <div className="err">{errors.phone}</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="field-group">
                                        <div className="field">
                                            <label htmlFor="industry">Industry</label>
                                            <select
                                                id="industry"
                                                value={form.industry}
                                                onChange={(e) => set("industry", e.target.value)}
                                                data-testid="contact-select-industry"
                                            >
                                                <option value="">Select your industry</option>
                                                {INDUSTRIES.map((i) => (
                                                    <option key={i} value={i}>
                                                        {i}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="field-group">
                                        <div className="field">
                                            <label>Products you&apos;re interested in</label>
                                            <div
                                                className="checkbox-row"
                                                data-testid="contact-checkboxes"
                                            >
                                                {PRODUCTS.map((p) => (
                                                    <label
                                                        key={p}
                                                        data-testid={`contact-checkbox-${p.toLowerCase()}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={form.products.includes(p)}
                                                            onChange={() => toggleProduct(p)}
                                                        />
                                                        {p}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="field-group">
                                        <div className="field">
                                            <label htmlFor="message">Tell us a bit more</label>
                                            <textarea
                                                id="message"
                                                value={form.message}
                                                onChange={(e) => set("message", e.target.value)}
                                                placeholder="What problem are you trying to solve right now?"
                                                data-testid="contact-textarea-message"
                                            />
                                        </div>
                                    </div>

                                    {serverError && (
                                        <div className="err" style={{ marginBottom: 12 }} data-testid="contact-server-error">
                                            {serverError}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-lg"
                                        disabled={submitting}
                                        data-testid="contact-submit-btn"
                                        style={{ width: "100%" }}
                                    >
                                        {submitting ? "Sending…" : "Book a Demo"}
                                    </button>
                                    <p
                                        data-testid="contact-response-promise"
                                        style={{
                                            fontSize: "0.88rem",
                                            marginTop: 16,
                                            textAlign: "center",
                                            color: "var(--accent)",
                                            fontWeight: 600,
                                        }}
                                    >
                                        ⏱ We respond to every demo request within 4 business hours.
                                    </p>
                                    <p
                                        className="muted"
                                        style={{
                                            fontSize: "0.8rem",
                                            marginTop: 10,
                                            textAlign: "center",
                                        }}
                                    >
                                        By submitting you agree to be contacted by Scitus.ai. We respect your inbox.
                                    </p>

                                    {/* Alternative channels */}
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr",
                                            gap: 12,
                                            marginTop: 24,
                                            paddingTop: 24,
                                            borderTop: "1px solid var(--border)",
                                        }}
                                        data-testid="contact-alt-channels"
                                    >
                                        <p
                                            className="muted"
                                            style={{ margin: 0, fontSize: "0.86rem", textAlign: "center" }}
                                        >
                                            Or talk to us another way:
                                        </p>
                                        <a
                                            href="https://wa.me/919677031328?text=Hi%20Scitus%2C%20I%27d%20like%20a%20quick%20demo%20of%20your%20products."
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="contact-whatsapp-btn"
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: 8,
                                                padding: "12px 18px",
                                                borderRadius: 10,
                                                background: "#25D366",
                                                color: "#fff",
                                                textDecoration: "none",
                                                fontWeight: 600,
                                                fontSize: "0.95rem",
                                            }}
                                        >
                                            💬 Chat on WhatsApp →
                                        </a>
                                        <p
                                            className="muted"
                                            style={{ margin: 0, fontSize: "0.78rem", textAlign: "center" }}
                                        >
                                            Most teams get a response within 2 hours on WhatsApp.
                                        </p>
                                        <a
                                            href="https://calendly.com/hello-scitus/30min"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="contact-calendly-btn"
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: 8,
                                                padding: "12px 18px",
                                                borderRadius: 10,
                                                border: "1px solid var(--border)",
                                                color: "var(--text)",
                                                textDecoration: "none",
                                                fontWeight: 600,
                                                fontSize: "0.95rem",
                                            }}
                                        >
                                            📅 Schedule on Calendly →
                                        </a>
                                        <p
                                            className="muted"
                                            style={{ margin: 0, fontSize: "0.78rem", textAlign: "center" }}
                                        >
                                            Prefer to pick a time directly?
                                        </p>
                                    </div>
                                </form>
                            )}
                        </div>

                        <div className="benefit-panel fade-up" data-testid="contact-benefit-panel">
                            <span className="eyebrow">Why book a demo?</span>
                            <h2 className="h2" style={{ fontSize: "1.75rem" }}>
                                30 minutes. Real numbers. No fluff.
                            </h2>
                            <ul className="benefit-list">
                                <li>
                                    <span className="icon-square">
                                        <Calendar size={18} />
                                    </span>
                                    <div className="body">
                                        Tailored walkthrough
                                        <small>
                                            We&apos;ll focus on the products
                                            relevant to your industry and team
                                            size.
                                        </small>
                                    </div>
                                </li>
                                <li>
                                    <span className="icon-square">
                                        <Sparkles size={18} />
                                    </span>
                                    <div className="body">
                                        Live AI copilot
                                        <small>
                                            See how Mativex, Vexen, and Karya
                                            actually answer your operating
                                            questions.
                                        </small>
                                    </div>
                                </li>
                                <li>
                                    <span className="icon-square">
                                        <ShieldCheck size={18} />
                                    </span>
                                    <div className="body">
                                        Rollout & data plan
                                        <small>
                                            Walk away with a clear plan for
                                            data migration, training, and
                                            launch.
                                        </small>
                                    </div>
                                </li>
                            </ul>

                            <hr className="divider" style={{ marginTop: 8 }} />

                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 8,
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        color: "var(--text)",
                                    }}
                                >
                                    <Mail size={16} color="var(--accent)" />
                                    <a
                                        href="mailto:hello@scitus.ai"
                                        data-testid="contact-email-link"
                                    >
                                        hello@scitus.ai
                                    </a>
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        color: "var(--text-muted)",
                                    }}
                                >
                                    <MapPin size={16} color="var(--accent)" />
                                    Available worldwide
                                </div>
                            </div>
                        </div>
                    </div>
                </FadeUpGroup>
            </div>
        </section>
    );
};

export default ContactPage;
